import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Customer, Account, InyovaGrowAccountStatus } from '@inyova/models';

import { Store } from '@ngrx/store';
import { take } from 'rxjs';
import * as StrategyActions from '@app/modules/strategy/strategy.actions';
import * as fromAppRoot from '../app.reducers';

import { GrowService } from '@app/modules/grow/services/grow.service';

@Component({
  selector: 'app-home',
  template: '',
  standalone: false
})
export class HomeComponent implements OnInit {
  constructor(
    private store: Store,
    private router: Router,
    private growService: GrowService
  ) {}

  ngOnInit(): void {
    this.store
      .select(fromAppRoot.selectCustomerAndCurrentAccount)
      .pipe(take(1))
      .subscribe((value) => {
        this.autoRoute(value.currentAccountDetail, value.customer);
      });
  }

  private autoRoute(currentAccountDetail: Account, customer: Customer): void {
    // early exit for no accounts
    if (!customer.email_confirmed) {
      void this.router.navigate(['/next/verify-email']);
      return;
    }

    if (customer.show_greenness_survey === 'greenness' && currentAccountDetail.step_status < 3) {
      void this.router.navigate(['/greenness-survey']);
      return;
    }
    if (!customer.temporary_accounts.length && customer.email && !this.router.url.includes('/grow')) {
      void this.router.navigate([customer.has_crowd_investor_account ? '/ci-overview' : '/product']);
      return;
    }

    if (currentAccountDetail?.kind === 'grow') {
      const newUrl = this.growService.getTargetUrlBasedOnStepStatus(currentAccountDetail, customer);
      if (newUrl && this.router.url !== newUrl) {
        this.router.navigate([newUrl]);
      }
      return;
    }

    // clear local for CI Account
    localStorage.removeItem('CIAccount');
    const step = currentAccountDetail.step_status;
    const accountFunded = currentAccountDetail?.customer_journey?.account_funded;
    const idVerificationCompleted = currentAccountDetail?.customer_journey?.id_verification_completed;
    const hasBankApplicationInProgress = customer.temporary_accounts.some(
      (account) => account.kind === 'grow' && account.step_status === InyovaGrowAccountStatus.BANK_APPLICATION_SENT
    );
    const appLocation = customer.app_location;
    const kind = currentAccountDetail.kind;

    if (currentAccountDetail?.kind === '3b' && hasBankApplicationInProgress && step <= 3) {
      this.router.navigate(['/complete-bank-setup']);
      return;
    }

    switch (true) {
      case step === 2:
        if (currentAccountDetail.kind !== '3a' || !this.router.url.includes('/3a')) {
          void this.router.navigate(['/strategy']);
          this.store.dispatch(StrategyActions.setTabIndex({ tabIndex: 5 }));
        }
        break;
      case step === 3 && !idVerificationCompleted && !accountFunded:
        if (currentAccountDetail.kind === '3a') {
          if (currentAccountDetail.onboarding_step <= 6) {
            void this.router.navigate(['/strategy']);
          } else {
            void this.router.navigate(['/next/fund']);
          }
        } else if (appLocation === 'ch') {
          if (kind !== 'kid') {
            void this.router.navigate(['/next/saxo-account/start-verification']);
          } else {
            void this.router.navigate(['/next/fund']);
          }
        } else {
          void this.router.navigate(['/strategy']);
        }
        break;

      case step >= 3 && idVerificationCompleted && !accountFunded:
        void this.router.navigate(['/next/fund']);
        break;
      case step >= 4 && idVerificationCompleted && accountFunded:
      case step === 5:
        void this.router.navigate(['/reports/dashboard']);
        break;
      default:
        if (appLocation === 'ch') {
          const maxStep = Math.max(...customer.accounts.map((account) => account.step_status));
          if (maxStep === 1) {
            void this.router.navigate(['/product']);
          }
        }
        this.store.dispatch(StrategyActions.setTabIndex({ tabIndex: currentAccountDetail.ql_onboarding_step }));
        void this.router.navigate(['/strategy']);
        break;
    }
  }
}
